import { useContext, useEffect, useMemo, useState } from 'react'
import Icon from 'common/Icon'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'
import getDay from 'date-fns/getDay'
import { utcToZonedTime } from 'date-fns-tz'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { generateDatesWithDayOfWeekFilter } from 'app/ProviderSearch/utils'
import {
  SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR,
  EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR,
} from 'app/ProviderSearch/constants'
import { dateFormats, DayOfWeek } from 'constants/common'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import {
  getCurrentZonnedTime,
  splitDateAndTime,
  isToday,
  isDateEqual,
  getHasFutureSlots,
  PG_DOW_TO_DAY_OF_WEEK,
} from 'utils/common'
import classes from './index.module.scss'
import classNames from 'classnames'
import { NAVY } from 'constants/colors'
import ProviderSlotContext from 'context/ProviderBooking/ProviderSlots'

function firstNonNullDate(columnDates: (Date | null)[]): Date | undefined {
  return columnDates.find((d): d is Date => d !== null)
}

function lastNonNullDate(columnDates: (Date | null)[]): Date | undefined {
  return [...columnDates].reverse().find((d): d is Date => d !== null)
}

type DatePaginatorProps = {
  selectedDate: Date
  onDateChange: (date: Date) => void
  className?: string
  btnClassName?: string
  dateColumnClass?: string
  upperDateFormat?: string
  lowerDateFormat?: string
  weekDayContainerClass?: string
  visibleDateRangeLimit?: number
  dayOfWeek?: DayOfWeek[]
  extendedBookingEnabled?: boolean
  extendedBookingLoading?: boolean
  highlightedDate?: Date
}

const DatePaginator: React.FC<DatePaginatorProps> = ({
  className = '',
  btnClassName = '',
  upperDateFormat = '',
  lowerDateFormat = '',
  selectedDate,
  onDateChange,
  weekDayContainerClass = '',
  visibleDateRangeLimit,
  dayOfWeek,
  extendedBookingEnabled = false,
  extendedBookingLoading = false,
  highlightedDate,
}) => {
  const { slotListLimit } = useContext(SlotDateContext)
  const displayAndStepLimit = visibleDateRangeLimit ?? slotListLimit
  const { timeZone } = useContext(TimeZoneContext)
  const currentDate = getCurrentZonnedTime(timeZone)
  const [dates, setDates] = useState<(Date | null)[]>([])
  const { nextAvailabilityForProviders } = useContext(ProviderSlotContext)
  const hasFutureSlots = getHasFutureSlots(nextAvailabilityForProviders || {})

  useEffect(() => {
    if (selectedDate) {
      setDates(
        generateDatesWithDayOfWeekFilter(
          selectedDate,
          displayAndStepLimit,
          dayOfWeek,
          timeZone,
          extendedBookingEnabled
        )
      )
    }
  }, [
    selectedDate,
    displayAndStepLimit,
    dayOfWeek,
    timeZone,
    extendedBookingEnabled,
  ])

  const isDowActive = !!dayOfWeek?.length

  const maxSlotDate = addDays(
    currentDate,
    // Use the extended cap while the treatment is resolving so navigation is
    // not clamped to 3 weeks prematurely.
    extendedBookingEnabled || extendedBookingLoading
      ? EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR
      : SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR
  )
  const lastNavColumnDate = lastNonNullDate(dates)
  const nextDatesRangeStartDate =
    isDowActive && dates.length > 0 && lastNavColumnDate
      ? addDays(lastNavColumnDate, 1)
      : addDays(selectedDate || new Date(), displayAndStepLimit)
  const isDisplayedDatePastMaxSlotDate =
    splitDateAndTime(maxSlotDate).date <
    splitDateAndTime(nextDatesRangeStartDate).date
  // Extended booking slides the window via getSlotWindowStart, so "can go next"
  // means any bookable day still sits after the last visible column.
  const hasMoreBookableDatesAfterVisible =
    !!lastNavColumnDate &&
    splitDateAndTime(lastNavColumnDate).date <
      splitDateAndTime(maxSlotDate).date

  const canAdvanceDowPage = useMemo(() => {
    if (!isDowActive || !timeZone || !dayOfWeek?.length) {
      return true
    }

    if (!lastNavColumnDate) {
      return false
    }

    const nextAnchor = addDays(lastNavColumnDate, 1)
    nextAnchor.setHours(0, 0, 0, 0)
    const nextPageDates = generateDatesWithDayOfWeekFilter(
      nextAnchor,
      displayAndStepLimit,
      dayOfWeek,
      timeZone,
      extendedBookingEnabled
    )

    return nextPageDates.some((d) => d !== null)
  }, [
    isDowActive,
    timeZone,
    dayOfWeek,
    lastNavColumnDate,
    displayAndStepLimit,
    extendedBookingEnabled,
  ])

  let isNextDisabled = isDisplayedDatePastMaxSlotDate && !hasFutureSlots

  if (isDowActive) {
    isNextDisabled = !canAdvanceDowPage
  } else if (extendedBookingEnabled || extendedBookingLoading) {
    isNextDisabled = !hasMoreBookableDatesAfterVisible && !hasFutureSlots
  }

  const previousSlotDate = () => {
    const todayCalendarStr = format(
      currentDate,
      dateFormats.YEAR_MONTH_DAY_FORMAT
    )
    const normalizedTodayStart = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      0,
      0,
      0,
      0
    )

    if (isDowActive && dates.length > 0) {
      const firstNavColumnDate = firstNonNullDate(dates)

      if (!firstNavColumnDate) {
        onDateChange(normalizedTodayStart)
        return
      }

      const matches: Date[] = []
      let cursor = subDays(firstNavColumnDate, 1)

      while (matches.length < displayAndStepLimit) {
        const cursorDayStr = format(cursor, dateFormats.YEAR_MONTH_DAY_FORMAT)

        if (cursorDayStr < todayCalendarStr) {
          break
        }

        const dow =
          PG_DOW_TO_DAY_OF_WEEK[getDay(utcToZonedTime(cursor, timeZone))]

        if (dayOfWeek.includes(dow)) {
          matches.unshift(cursor)
        }

        cursor = subDays(cursor, 1)
      }

      onDateChange(matches[0] ?? normalizedTodayStart)
      return
    }

    const previousStartSlotDate = subDays(selectedDate, displayAndStepLimit)
    const previousDayStr = format(
      previousStartSlotDate,
      dateFormats.YEAR_MONTH_DAY_FORMAT
    )
    onDateChange(
      previousDayStr >= todayCalendarStr
        ? previousStartSlotDate
        : normalizedTodayStart
    )
  }

  const nextSlotDate = () => {
    if (isDowActive && dates.length > 0) {
      const lastNav = lastNonNullDate(dates)

      if (!lastNav) {
        return
      }

      const nextStart = addDays(lastNav, 1)

      nextStart.setHours(0, 0, 0, 0)
      onDateChange(nextStart)
      return
    }

    const nextStart = addDays(selectedDate || new Date(), displayAndStepLimit)
    nextStart.setHours(0, 0, 0, 0)
    onDateChange(nextStart)
  }

  const mobilePaginatorDate = firstNonNullDate(dates)

  // Timezone format would not be required here because startSlotDate already has the difference calculated for it
  return (
    <div className={`d-flex align-items-center flex-nowrap ${className}`}>
      <button
        type="button"
        aria-label="show more previous slots"
        disabled={isToday(selectedDate, timeZone)}
        onClick={previousSlotDate}
        className={classNames(
          'btn btn-none',
          classes.date_paginator_button,
          btnClassName,
          'flex-center'
        )}
      >
        <Icon icon="datePrevArrow" className="fa-min-md" fill={NAVY} />
      </button>
      <div
        className={classNames(
          'row no-gutters d-none d-lg-flex flex-fill flex-nowrap',
          weekDayContainerClass
        )}
      >
        {dates.map((date: Date | null, index: number) => {
          const isHighlighted =
            !!date && !!highlightedDate && isDateEqual(date, highlightedDate)

          return (
            <div
              key={date ? date.getTime() : `masked-col-${index}`}
              className={classNames('col', classes.date_column_container)}
            >
              <div
                className={classNames(
                  '~mx-auto',
                  isHighlighted && '~bg-sunrise-30 ~rounded-3xs ~max-w-[65px]'
                )}
              >
                {date ? (
                  <>
                    <div>
                      {format(date, dateFormats.DAY_FORMAT).toUpperCase()}
                    </div>
                    <div className="font-weight-medium">
                      {lowerDateFormat
                        ? format(date, dateFormats[lowerDateFormat])
                        : format(date, dateFormats.MONTH_DAY_FORMAT)}
                    </div>
                  </>
                ) : (
                  <div className={classes.date_column_masked}>—</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="d-flex d-lg-none flex-fill justify-content-center">
        {dates?.length > 0 && mobilePaginatorDate && (
          <div className="text-center text-nowrap">
            {upperDateFormat && (
              <div className="fs-14 text-date-light font-weight-bold">
                {format(
                  mobilePaginatorDate,
                  dateFormats[upperDateFormat]
                ).toUpperCase()}
              </div>
            )}
            <div className="fs-18">
              {format(
                mobilePaginatorDate,
                lowerDateFormat
                  ? dateFormats[lowerDateFormat]
                  : dateFormats.MONTH_DAY_FORMAT
              )}
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label="show more next slots"
        disabled={isNextDisabled}
        onClick={nextSlotDate}
        className={classNames(
          'btn btn-none',
          classes.date_paginator_button,
          btnClassName,
          'flex-center'
        )}
      >
        <Icon icon="dateNextArrow" className="fa-min-md" fill={NAVY} />
      </button>
    </div>
  )
}

export default DatePaginator
